import { useCallback, useMemo, useState } from "react";

import { cn } from "../../lib";
import {
  DialogContentPrimitive,
  DialogOverlayPrimitive,
  DialogPortalPrimitive,
  DialogRootPrimitive,
} from "../../primitives";
import {
  DialogContext,
  dialogOverlayClasses,
  dialogOverlayLayer,
  dialogSizeClasses,
  dialogSurfaceAnimation,
  dialogSurfaceBase,
} from "./lib";
import type { DialogRootProps } from "./types";

/**
 * Dialog.Root
 *
 * Controlled-only centered modal surface. Composes a Radix (web) /
 * `@rn-primitives/dialog` (native) primitive with our token-driven surface
 * styling, viewport-aware width, and `dismissible` plumbing.
 *
 * Imperative open/close is intentionally out of scope — see DS-046
 * AdaptiveModal for a global imperative stack.
 */
export const DialogRoot = ({
  open,
  onOpenChange,
  size = "md",
  dismissible = true,
  divider = true,
  overlay,
  portalHost,
  className,
  testID,
  children,
}: DialogRootProps) => {
  const requestClose = useCallback(() => onOpenChange?.(false), [onOpenChange]);
  const [bodyOverflowing, setBodyOverflowing] = useState(false);
  const [hasBody, setHasBody] = useState(false);

  const contextValue = useMemo(
    () => ({
      size,
      dismissible,
      divider,
      bodyOverflowing,
      setBodyOverflowing,
      hasBody,
      setHasBody,
      requestClose,
    }),
    [size, dismissible, divider, bodyOverflowing, hasBody, requestClose],
  );

  // `overlay === null` opts out entirely. A node replaces the default scrim's
  // look, drawn inside the overlay's layer, which still covers the page and
  // stops it scrolling (as on native); `undefined` keeps the default.
  const overlayNode =
    overlay === null ? null : (
      <DialogOverlayPrimitive
        className={overlay === undefined ? dialogOverlayClasses : dialogOverlayLayer}
      >
        {overlay}
      </DialogOverlayPrimitive>
    );

  return (
    <DialogRootPrimitive open={open} onOpenChange={onOpenChange}>
      <DialogPortalPrimitive portalHost={portalHost}>
        {/* Context provider lives INSIDE the portal so it crosses the native
            portal boundary: the native portal re-roots children to a
            PortalHost, which would otherwise strip context from the parts. */}
        <DialogContext.Provider value={contextValue}>
          {overlayNode}
          <DialogContentPrimitive
            testID={testID}
            disableDismiss={!dismissible}
            className={cn(
              dialogSurfaceBase,
              dialogSurfaceAnimation,
              dialogSizeClasses[size],
              className,
            )}
          >
            {children}
          </DialogContentPrimitive>
        </DialogContext.Provider>
      </DialogPortalPrimitive>
    </DialogRootPrimitive>
  );
};

DialogRoot.displayName = "Dialog.Root";
